import { useCallback, useMemo, useRef, useState } from "react";

export type StatusIdleResult = {
  isIdle: true;
  isLoading?: false;
  isError?: false;
  error?: never;
};
export type StatusLoadingResult = {
  isIdle?: false;
  error?: never;
  isLoading: true;
  isError?: false;
};
export type StatusErrorResult<E> = {
  isIdle?: false;
  isLoading?: false;
  isError: true;
  error: E;
};
export type StatusSuccessResult<R> = {
  isError?: false;
  isLoading?: false;
  isIdle?: false;
  value: R;
};

export type ThrowableStatus<R = void, E = Error> =
  | StatusIdleResult
  | StatusLoadingResult
  | StatusErrorResult<E>
  | StatusSuccessResult<R>;

function isSuccessStatus<R = void, E = Error>(
  status: ThrowableStatus<R, E>,
): status is StatusSuccessResult<R> {
  return !status.isError && !status.isIdle && !status.isLoading;
}

function createDefaultStatus(): StatusIdleResult {
  return { isIdle: true };
}

type StatusReturn<R, E> = {
  getStatus: () => Readonly<ThrowableStatus<R, E>>;
  useStatus: () => Readonly<ThrowableStatus<R, E>>;
  setIdle: () => void;
  setLoading: () => void;
  setError: (error: E) => void;
  setSuccess: (value: R) => void;
};

/** A hook for managing an actions lifecycle.
 * @deprecated Please don't use this. It is a half baked idea.
 * @example
 * type Example = {
 *  name: string
 * }
 *
 * function App(): JSX.Element {
 *    const store = useStatusStore();
 *    const status = store.useState(); // Use where you need reactivity
 *
 *    function handleClick(): Promise<void> {
 *      store.setLoading();
 *      const result = await requestData();
 *      if(result.isError) return store.setError(result.error);
 *      store.setSuccess(result.value);
 *    }
 *
 *    return status.isLoading
 *      ? "Loading"
 *      : status.isError
 *      ? status.error.message
 *      : useStatus.isSuccessStatus(status)
 *      ? status.value.name
 *      : null
 * }
 */
function useStatusStore<R = void, E = Error>(): StatusReturn<R, E> {
  type StatusReturnWithGenerics = StatusReturn<R, E>;
  type UseStatusWithGenerics = ThrowableStatus<R, E>;
  const status = useRef<UseStatusWithGenerics>(createDefaultStatus());
  const subs = useRef<Set<(update: UseStatusWithGenerics) => void>>(new Set());

  const useStatus = useCallback<StatusReturnWithGenerics["useStatus"]>(
    function useStatus() {
      const state = useState(status.current);
      subs.current.add(state[1]);
      return state[0];
    },
    [],
  );

  const getStatus = useCallback<StatusReturnWithGenerics["getStatus"]>(() => {
    return status.current;
  }, []);

  const update = useCallback<(update: UseStatusWithGenerics) => void>(
    function onUpdate(update) {
      subs.current.forEach((sub) => sub(update));
    },
    [],
  );

  const setIdle = useCallback<StatusReturnWithGenerics["setIdle"]>(
    function onIdle() {
      update({ isIdle: true, isError: false, isLoading: false });
    },
    [update],
  );

  const setLoading = useCallback<StatusReturnWithGenerics["setLoading"]>(
    function onLoading() {
      update({ isIdle: false, isError: false, isLoading: true });
    },
    [update],
  );

  const setError = useCallback<StatusReturnWithGenerics["setError"]>(
    function onError(error) {
      update({ isLoading: false, isIdle: false, isError: true, error });
    },
    [update],
  );

  const setSuccess = useCallback<StatusReturnWithGenerics["setSuccess"]>(
    function onSuccess(value) {
      update({
        isIdle: false,
        isLoading: false,
        isError: false,
        value,
      });
    },
    [update],
  );

  return useMemo(
    () => ({
      useStatus,
      getStatus,
      setIdle,
      setError,
      setLoading,
      setSuccess,
    }),
    [getStatus, setError, setIdle, setLoading, setSuccess, useStatus],
  );
}

useStatusStore.isSuccessStatus = isSuccessStatus;

export { useStatusStore };
